import ButtonSizeEnum from '@inter-ikea-kompis/component-button/lib/enums/ButtonSizeEnum';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { KompisMiniConfigurationSummary } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { NightLightsSystem } from '@inter-ikea-storageone/range';
import { addBreadcrumb } from '@sentry/react';
import { reportOnSummaryClicked } from 'analytics/reporters';
import GenerateAssembly from 'components/GenerateAssembly/GenerateAssembly';
import Menu from 'components/Header/Menu/Menu';
import { useAvailability } from 'contexts/availability';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useScreenshot } from 'contexts/screenshot';
import { showHelpLinesCollisionWarningSheet } from 'ducks/helpLines';
import { useAppDispatch } from 'ducks/hooks';
import {
  hideMeasurements,
  isCheckoutAvailable,
  isMeasurementsVisible,
  isRoomConfigurationActive,
  lightScene,
  showDarkSceneWhenGoingBack,
  showMeasurementsWhenGoingBack,
  showProductMenuHeader
} from 'ducks/layout';
import { getMarketProducts, getShoppingItemsInScene, selectGroup } from 'ducks/products';
import { getVpcCode } from 'ducks/vpc';
import { showBillyCornerStabilitySheet } from 'rangeConfig/billy/features/BillyCornerStability/BillyCornerStabilityFeature';
import { shouldShowCornerStabilitySheet } from 'rangeConfig/billy/features/BillyCornerStability/BillyCornerStabilitySheet';
import {
  wasDismissed as coverCapsSheetDismissed,
  showCoverCapsSheet
} from 'rangeConfig/pax/features/CoverCaps/CoverCapsFeature';
import { shouldShowCoverCapsSheet } from 'rangeConfig/pax/features/CoverCaps/CoverCapsUtils';
import shouldWarnAboutFramesOutsideOfHelpLines from 'rangeConfig/shared/features/HelpLines/shouldWarnAboutFramesOutsideOfHelpLines';
import {
  showRecommendHandles,
  wasDismissed,
  wasDismissedAndContinue
} from 'rangeConfig/shared/features/RecommendHandles/RecommendHandles';
import shouldRecommendHandles from 'rangeConfig/shared/features/RecommendHandles/shouldRecommendHandles';
import {
  setStabilityDismissedAndContinue,
  showStabilitySheet,
  stabilityWasDismissedAndContinue
} from 'rangeConfig/shared/features/StabilityValidation/StabilityValidation';
import { shouldShowStabilityInfo } from 'rangeConfig/shared/features/StabilityValidation/StabilityValidationUtils';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import useIsRtl from 'hooks/useIsRtl';
import { useVpc } from 'hooks/useVpc';
import RtlStyles from 'i18n/rtl';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import media from 'ui/media';
import { isIkeaApp, isKiosk, isMobile } from 'utils/platform';
import { getQuery } from 'utils/queryUtils';
import LogoBackButton from './BackButton';
import { ComponentIds } from '@inter-ikea-core/core';
import {
  appIntegration,
  getKioskIntegration,
  isUpptackaActive,
  isSpaceEnabled
} from 'utils/integration';
import SaveForLaterButton from './SaveForLaterButton';
import { getProductEntities } from 'ducks/core';
import { useServiceSettings } from 'contexts/serviceSettings';
import { RenderLocation } from 'features/Feature';
import Features from 'components/Features/Features';

const Header = styled.div`
  position: absolute;
  height: ${({ theme }) =>
    isMobile() ? theme.layout.mobileHeaderHeight : theme.layout.headerHeight};
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 20;
  top: 0;
  right: 0;
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  padding: 0 ${({ theme }) => theme.space.l};
  pointer-events: none;
  ${media.mobile`
    width: 100%;
    padding: 0;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem 0;
  `}

  &[data-hidden='true'] {
    opacity: 0;
    pointer-events: none;
  }
  &[data-rtl='true'] {
    flex-direction: row-reverse;
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  pointer-events: auto;
  ${media.mobile`
    margin: 0 0 0 1rem;
  `}
  &[data-rtl='true'] {
    flex-direction: row-reverse;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-grow: 1;
  ${media.mobile`
    flex-direction: row-reverse;
    justify-content: space-between;
    margin: 0 1rem 0 0;
  `}
  &[data-rtl='true'] {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }
`;

const SceneBar = styled.div`
  display: flex;
  pointer-events: none;
  z-index: 20;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - ${({ theme }) => theme.layout.sidebarWidth});
`;

const FeatureContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.s};
  pointer-events: auto;
`;

const KompisConfigurationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
  pointer-events: auto;
  ${media.mobile`
    margin-right: 0;
  `}
  &[data-rtl='true'] {
    margin-left: 1rem;
    ${media.mobile`
      margin-right: 0;
      margin-left: 0;
    `}
  }
`;

const DesktopMenu = () => {
  return (
    <RtlStyles>
      <Menu />
    </RtlStyles>
  );
};

const Actions = () => {
  const enableSpacePlanning = isSpaceEnabled();
  const roomConfigurationActive = useSelector(isRoomConfigurationActive);

  const isRtl = useIsRtl();

  return (
    <LeftWrapper data-testid="left-wrapper" data-rtl={isRtl}>
      {isMobile() && (
        <>
          {!isIkeaApp() && <DesktopMenu />}
          {!enableSpacePlanning && !roomConfigurationActive && <LogoBackButton />}
        </>
      )}
      {!isMobile() && !enableSpacePlanning && !roomConfigurationActive && <LogoBackButton />}
    </LeftWrapper>
  );
};

interface Props {
  hideMenu?: boolean;
}

const generateAssembly =
  getQuery().get('generateInteriorAssembly') === 'true' ||
  getQuery().get('generateFrontAssembly') === 'true';

const kioskIntegration = getKioskIntegration();

const ConfigurationHeader = ({ hideMenu }: Props) => {
  const coreApi = useCore();
  const isRtl = false; // Force header to right regardless of market for now (previously used useIsRtl() but it was decided this is more consistent)
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { translations, dexfSettings } = useServiceSettings();
  const { images } = useScreenshot();
  const productsInScene = useSelector(getShoppingItemsInScene);
  const { updateStoreAvailabilityForShoppingProducts } = useAvailability();
  const enableSpacePlanning = isSpaceEnabled();
  const vpc = useVpc();

  // This is a hack from kompis that reduces the text length of the family price text
  // We will need to remove this once kompis have made a long term solution for the component
  const newDexfSettings = {
    ...dexfSettings,
    kompis: {
      ...dexfSettings.kompis,
      priceSettings: {
        ...dexfSettings.kompis.priceSettings,
        temporaryPricePeriod: 'PeriodNone'
      }
    }
  };

  const { shoppingProducts, shoppingItems } = useFetchShoppingProducts(productsInScene);
  const vpcCode = useSelector(getVpcCode);
  const recommendHandleDismissed = useSelector(wasDismissed);
  const recommendHandlesDismissedAndContinue = useSelector(wasDismissedAndContinue);
  const stabilityDismissedAndContinue = useSelector(stabilityWasDismissedAndContinue);
  const allProducts = useSelector(getMarketProducts);
  const shoppingBagEntities = useSelector(getShoppingItemsInScene);
  const checkoutAvailable = useSelector(isCheckoutAvailable);
  const roomConfigurationActive = useSelector(isRoomConfigurationActive);
  const measurementVisible = useSelector(isMeasurementsVisible);
  const coverCapsDismissed = useSelector(coverCapsSheetDismissed);

  const [plannerLoaded, setPlannerLoaded] = useState(false);
  const [ecoFeeModalVisible, setEcoFeeModalVisible] = useState<'ecoFeeCard' | null>(null);
  const [loading, setLoading] = useState(false);

  const [goToSummary, setGoToSummary] = useState(false);

  const {
    plannerConfig: { features }
  } = usePlannerConfig();

  const hasFeature = (id: string) => features.map(f => f.id).includes(id);

  const recommendHandlesFeature = hasFeature('RecommendHandles');
  const stabilityValidationFeature = hasFeature('StabilityValidation');
  const billyCornerStabilityFeature = hasFeature('BillyCornerStability');
  const coverCapsFeature = hasFeature('CoverCaps');

  useEffect(() => {
    if (goToSummary) {
      reportOnSummaryClicked({ timeStamp: new Date() });
      setLoading(false);
      setGoToSummary(false);

      if (vpcCode) {
        kioskIntegration.summaryClicked(vpcCode);
        appIntegration.summaryClicked(vpcCode);
      }

      if (isUpptackaActive()) return;
      dispatch(selectGroup(''));
      dispatch(showProductMenuHeader());
      history.push(AppRoutes.SUMMARY);
    }
  }, [history, goToSummary, vpcCode, dispatch]);

  const hideMeasurementsForSummary = () => {
    dispatch(hideMeasurements());
    dispatch(showMeasurementsWhenGoingBack(measurementVisible));
  };

  const disableNightModeForSummary = () => {
    const nightLightSystem = coreApi.getSystem<NightLightsSystem>('NightLightsSystem');
    if (nightLightSystem) {
      dispatch(showDarkSceneWhenGoingBack(nightLightSystem.isEnabled));
      dispatch(lightScene());
      coreApi.getSystem<NightLightsSystem>('NightLightsSystem')?.disable();
    }
  };

  const onSummaryClicked = () => {
    kioskIntegration.preSummaryClicked();
    coreApi.entities.unselect();

    addBreadcrumb({ message: 'Go to Summary clicked' });

    if (shouldWarnAboutFramesOutsideOfHelpLines(coreApi)) {
      dispatch(showHelpLinesCollisionWarningSheet(true));
    } else if (
      recommendHandlesFeature &&
      !recommendHandleDismissed &&
      shouldRecommendHandles(coreApi)
    ) {
      dispatch(showRecommendHandles());
    } else if (
      coverCapsFeature &&
      !coverCapsDismissed &&
      shouldShowCoverCapsSheet(coreApi, allProducts)
    ) {
      dispatch(showCoverCapsSheet());
    } else if (stabilityValidationFeature && shouldShowStabilityInfo(coreApi)) {
      dispatch(showStabilitySheet());
    } else if (billyCornerStabilityFeature && shouldShowCornerStabilitySheet(coreApi)) {
      dispatch(showBillyCornerStabilitySheet());
    } else {
      completeGoToSummarySequence();
    }
  };

  const completeGoToSummarySequence = async () => {
    updateStoreAvailabilityForShoppingProducts();
    hideMeasurementsForSummary();
    disableNightModeForSummary();

    if (!vpcCode || !images.length) {
      setLoading(true);
      await vpc.save();
    }
    setGoToSummary(true);
  };

  // Callback effect after clicking summary and spawning a sheet
  useEffect(() => {
    const triggers = [
      recommendHandlesDismissedAndContinue,
      stabilityDismissedAndContinue,
      coverCapsDismissed
    ];

    if (triggers.includes(true)) {
      dispatch(setStabilityDismissedAndContinue(false));
      completeGoToSummarySequence();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendHandlesDismissedAndContinue, stabilityDismissedAndContinue, coverCapsDismissed]);

  /**
   * ToDo
   * Move to separate file
   */

  const postMessageEvent = (target: Window, action: any, data?: any) => {
    const payload = { action, ...data };

    target.postMessage(payload);
  };

  const onSpaceComplete = () => {
    //export enetities from the scene

    // First get all entities that we need to send to space
    const productEntitiesInScene = coreApi.entities.getAll();
    const entityGroupsInScene = coreApi.entities.getByComponent(
      ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID
    );

    const entitiesInScene = [...productEntitiesInScene, ...entityGroupsInScene];

    // Then use serializer service from core to export them as a json string
    const serializedEntities = coreApi
      .getServices()
      .serializer.hierarchiesToJsonString(entitiesInScene);

    // Finally post the message to the parent window that opened the iFrame (space in this case)
    postMessageEvent(window.parent, 'onConfiguratorDone', {
      payload: {
        serializedEntities
      }
    });

    coreApi.resetSceneSpace();
  };
  /** END ToDo */

  const onEcoFeeClicked = (event: any) => {
    setEcoFeeModalVisible(event.detail.visibleModal);
  };

  const onPlannerLoaded = useCallback(() => {
    postMessageEvent(window.parent, 'onConfiguratorReady');
  }, []);

  const onPlannerReload = useCallback((e: any) => {
    if (e.data.action === 'onStartConfigurator') {
      postMessageEvent(window.parent, 'onConfiguratorReady');
    }
  }, []);

  // ToDo refactor
  useEffect(() => {
    if (enableSpacePlanning) {
      coreApi.ipexViewer?.on_current_scene_ready?.().then(() => onPlannerLoaded());

      setPlannerLoaded(true);
    }
  }, [coreApi.ipexViewer, enableSpacePlanning, onPlannerLoaded]);

  useEffect(() => {
    if (enableSpacePlanning && plannerLoaded) {
      window.addEventListener('message', e => onPlannerReload(e));
    }

    return () => {
      if (enableSpacePlanning) {
        window.removeEventListener('message', e => onPlannerReload(e));
      }
    };
  }, [enableSpacePlanning, onPlannerReload, plannerLoaded]);

  const buttonSize = isMobile() ? ButtonSizeEnum.small : ButtonSizeEnum.medium;

  const productEntitiesInScene = useSelector(getProductEntities);

  const showSaveForLaterButton =
    productEntitiesInScene.length > 0 &&
    getQuery().get('enableSaveForLater') === 'true' &&
    isKiosk() &&
    isUpptackaActive();

  return (
    <Header>
      {generateAssembly ? (
        <>
          <Wrapper>
            <GenerateAssembly />
            <DesktopMenu />
          </Wrapper>
        </>
      ) : (
        <>
          {!isRtl && (
            <SceneBar>
              <Actions />
              <FeatureContainer>
                <Features location={RenderLocation.Header} />
                {showSaveForLaterButton && <SaveForLaterButton />}
              </FeatureContainer>
            </SceneBar>
          )}
          <Wrapper
            data-rtl={isRtl}
            data-testid="header-total-price"
            data-shoppingitems={JSON.stringify(shoppingItems)}
          >
            <KompisConfigurationWrapper data-rtl={isRtl}>
              {!enableSpacePlanning && (
                <KompisMiniConfigurationSummary
                  shoppingProducts={shoppingProducts}
                  settings={newDexfSettings}
                  translations={translations.kompis}
                  theme={SkapaTheme}
                  buttonLabel={formatMessage({ id: 'storageone.finalizeSolutionBtn' })}
                  animatedPrice
                  hideWhatsIncludedIcon
                  loading={loading}
                  visibleModal={ecoFeeModalVisible as any}
                  onModalOpen={event => onEcoFeeClicked(event)}
                  onModalClose={event => onEcoFeeClicked(event)}
                  disableLinks={isKiosk()}
                  onNext={onSummaryClicked}
                  disabled={
                    shoppingBagEntities.length === 0 ||
                    !checkoutAvailable ||
                    roomConfigurationActive
                  }
                  data-testid={'mini-configuration-summary'}
                  buttonSize={buttonSize}
                />
              )}
              {enableSpacePlanning && (
                <KompisMiniConfigurationSummary
                  shoppingProducts={shoppingProducts}
                  settings={newDexfSettings}
                  translations={translations.kompis}
                  theme={SkapaTheme}
                  buttonLabel={formatMessage({ id: 'storageone.done' })}
                  animatedPrice
                  hideWhatsIncludedIcon
                  hideButtonIcon
                  loading={loading}
                  visibleModal={ecoFeeModalVisible as any}
                  onModalOpen={event => onEcoFeeClicked(event)}
                  onModalClose={event => onEcoFeeClicked(event)}
                  disableLinks={isKiosk()}
                  onNext={onSpaceComplete}
                  disabled={isKiosk()}
                  data-testid={'mini-configuration-summary'}
                  buttonSize={buttonSize}
                />
              )}
            </KompisConfigurationWrapper>
            {!isMobile() && !hideMenu && !roomConfigurationActive && <DesktopMenu />}
          </Wrapper>
          {isRtl && (
            <SceneBar dir="ltr">
              <Actions />
              <FeatureContainer>
                <Features location={RenderLocation.Header} />
                {showSaveForLaterButton && <SaveForLaterButton />}
              </FeatureContainer>
            </SceneBar>
          )}
        </>
      )}
    </Header>
  );
};

export default ConfigurationHeader;
